import { Capacitor } from '@capacitor/core';
import { trace } from './traceLog';

/**
 * One-shot retry for idempotent, read-only fetches that failed on a transient
 * connectivity blip. WEB ONLY — see the platform gate below.
 *
 * Why this exists (2026-09-29 web wake investigation): a captured production
 * console log showed a ~30s window where an auth token refresh failed with
 * auth/network-request-failed, the Firestore SDK declared itself offline, and
 * every in-flight one-shot read failed INSTANTLY with "client is offline".
 * The SDK reconnected on its own seconds later, but nothing re-ran the reads
 * that had already failed — so pages silently kept degraded state (or sat on
 * a spinner) until a manual refresh. The fix is not to cycle the network (the
 * transport healed itself) but to give those reads one second chance.
 *
 * Contract — agreed 2026-09-29, do not widen it casually:
 * - READS ONLY. Never wrap writes, offer responses, payments, Stripe calls,
 *   callables with side effects, or admin actions: a retry there can double
 *   an action. transientReadRetry.test.js pins the exact list of importing
 *   modules; extending it is a reviewed decision, not a drive-by.
 * - WEB ONLY. On native (Capacitor) the helper runs the read exactly once
 *   and rethrows — behavior identical to not using the helper at all. The
 *   native wake path has its own mitigation (the resume reconnect cycle in
 *   App.js) and stays unchanged in this release. The gate lives HERE, in one
 *   place, so no call site can forget it.
 * - Retries EXACTLY ONCE, after ~600-1000ms with jitter. A single retry
 *   covers sub-second blips; longer outages must surface to the caller so
 *   the page keeps its real degraded/recovery state instead of a spinner.
 * - Retries ONLY recognized transient connectivity failures. Anything else
 *   (permission-denied, not-found, invalid-argument, plain bugs) rethrows
 *   immediately — retrying those masks real errors and delays their surfacing.
 * - Never swallows: if the retry also fails, the SECOND error is thrown and
 *   the caller's existing error handling runs exactly as before. Callers keep
 *   last-good data / fallbacks themselves.
 * - Traces every retry (read_retry_start / read_retry_success /
 *   read_retry_failed) into the bounded local wake-trace buffer — label and
 *   sanitized error code only, never user data or raw error payloads — so
 *   the next field incident shows whether the retry is working.
 */

const TRANSIENT_CODES = new Set([
  // Firestore SDK while it considers itself offline ("Failed to get document
  // because the client is offline." carries this code).
  'unavailable',
  // firebase/auth token refresh losing the network mid-request — the exact
  // failure that opened the captured incident's offline window.
  'auth/network-request-failed',
]);

export function isTransientConnectivityError(err) {
  if (!err) return false;
  if (TRANSIENT_CODES.has(err.code)) return true;
  // Offline cache-served results that our guards converted into errors
  // (recoverableLoad.js throwIfOfflineEmpty / throwIfOfflineMiss): the SDK
  // "succeeded" from cache, but only because it couldn't reach the server —
  // connectivity-transient by definition, so the one retry applies.
  if (err.name === 'OfflineResultError') return true;
  // Defense for SDK paths that surface the offline failure without a code.
  const message = typeof err.message === 'string' ? err.message : '';
  return /client is offline/i.test(message);
}

const RETRY_DELAY_BASE_MS = 600;
const RETRY_DELAY_JITTER_MS = 400;

/**
 * Run `readFn` (an async, side-effect-free fetch); on web, if it fails with a
 * transient connectivity error, wait ~600-1000ms and run it once more.
 *
 * @param {string} label   short trace key, e.g. 'signup-check' — appears in
 *                         the local diagnostic trace, never sent anywhere.
 * @param {Function} readFn  () => Promise — must be safe to call twice.
 * @param {{delayMs?: number}} [opts]  test-only delay override.
 */
export async function withTransientRetry(label, readFn, opts = {}) {
  try {
    return await readFn();
  } catch (err) {
    if (Capacitor.isNativePlatform()) throw err; // web-only; native unchanged
    if (!isTransientConnectivityError(err)) throw err;
    const delayMs = opts.delayMs != null
      ? opts.delayMs
      : RETRY_DELAY_BASE_MS + Math.random() * RETRY_DELAY_JITTER_MS;
    trace('read_retry_start', { label, code: err?.code || 'offline', delayMs: Math.round(delayMs) });
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const result = await readFn();
      trace('read_retry_success', { label });
      return result;
    } catch (retryErr) {
      trace('read_retry_failed', { label, code: retryErr?.code || 'unknown' });
      throw retryErr;
    }
  }
}
